import { create } from "zustand";

/**
 * Certificate NFT Issuer Store (v1)
 * 
 * Manages ESG certificate NFT issuance, revocation, blockchain anchoring,
 * and certificate level determination based on ESG scores.
 */
export interface CertificateNFTStoreState {
  companyID: string;
  esgScore: number; // 0-100
  certificateLevel: "bronze" | "silver" | "gold" | "platinum";
  issuanceTimestampISO: string;
  expiryTimestampISO: string;
  blockchainTxHash: string;
  verifierRegion: "EU" | "EEA" | "National";
  revocationRisk: number; // 0-1

  // Actions
  setCompanyID: (id: string) => void;
  setEsgScore: (score: number) => void;
  setVerifierRegion: (region: "EU" | "EEA" | "National") => void;

  // Actions
  issueCertificate: () => void;
  revokeCertificate: () => void;

  // Derived outputs
  getIssuanceCostEUR: () => number;
  getValidityDays: () => number;
  getRevocationProbability: () => number;

  // Export
  exportCertificateEnvelope: () => void;
}

export const useCertificateNFTStore = create<CertificateNFTStoreState>((set, get) => {
  const determineLevel = (score: number): "bronze" | "silver" | "gold" | "platinum" => {
    if (score < 40) return "bronze";
    if (score < 60) return "silver";
    if (score < 80) return "gold";
    return "platinum";
  };

  const generateTxHash = (): string => {
    return `0x${Array.from({ length: 64 }, () => Math.floor(Math.random() * 16).toString(16)).join("")}`;
  };

  return {
    companyID: "",
    esgScore: 0,
    certificateLevel: "bronze",
    issuanceTimestampISO: "",
    expiryTimestampISO: "",
    blockchainTxHash: "",
    verifierRegion: "EU",
    revocationRisk: 0,

    setCompanyID: (id) => set({ companyID: id }),
    setEsgScore: (score) => set({ esgScore: Math.max(0, Math.min(100, score)) }),
    setVerifierRegion: (region) => set({ verifierRegion: region }),

    issueCertificate: () => {
      const state = get();
      if (!state.companyID || state.esgScore === 0) return;

      const issuanceTimestampISO = new Date().toISOString();
      const expiryDate = new Date(issuanceTimestampISO);
      expiryDate.setDate(expiryDate.getDate() + 365);
      const expiryTimestampISO = expiryDate.toISOString();
      const blockchainTxHash = generateTxHash();
      const certificateLevel = determineLevel(state.esgScore);

      set({
        issuanceTimestampISO,
        expiryTimestampISO,
        blockchainTxHash,
        certificateLevel,
        revocationRisk: 0,
      });
    },

    revokeCertificate: () => {
      set({ revocationRisk: 1.0 });
    },

    getIssuanceCostEUR: () => {
      return get().esgScore * 0.8;
    },

    getValidityDays: () => {
      const state = get();
      if (!state.issuanceTimestampISO || !state.expiryTimestampISO) return 0;
      const start = new Date(state.issuanceTimestampISO);
      const end = new Date(state.expiryTimestampISO);
      return Math.floor((end.getTime() - start.getTime()) / 86400000);
    },

    getRevocationProbability: () => {
      const state = get();
      return state.revocationRisk * ((100 - state.esgScore) / 100);
    },

    exportCertificateEnvelope: () => {
      const state = get();
      const report = {
        companyID: state.companyID,
        esgScore: state.esgScore,
        certificateLevel: state.certificateLevel,
        issuanceTimestampISO: state.issuanceTimestampISO,
        expiryTimestampISO: state.expiryTimestampISO,
        blockchainTxHash: state.blockchainTxHash,
        verifierRegion: state.verifierRegion,
        revocationProbability: state.getRevocationProbability(),
        validityDays: state.getValidityDays(),
        issuanceCostEUR: state.getIssuanceCostEUR(),
      };

      const dateStr = new Date().toISOString().split("T")[0].replace(/-/g, "");
      const blob = new Blob([JSON.stringify(report, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `certificateEnvelope_${dateStr}.json`;
      a.click();
      URL.revokeObjectURL(url);
    },
  };
});











